/**
 * <AttentionGuardConsent /> — one-time prompt before the Rapid Installation
 * step. Asks the user whether the camera should gently call them back if
 * their eyes drift from the fixation dot for >3 s.
 *
 * Choice persists in localStorage. Opt-out is the privacy-safe default.
 * Once consent is given (or declined), this prompt never re-appears.
 *
 * Storage keys:
 *   reclaim_attention_guard_decided  — 'true' once the user has chosen
 *   reclaim_attention_guard_enabled  — 'true' if the user opted in
 */
import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import {
  JOURNEY_PALETTE as P,
  JOURNEY_FONT_HEAD,
  JOURNEY_FONT_BODY,
  JOURNEY_FONT_LABEL,
} from "./journeyTheme";

const KEY_DECIDED = "reclaim_attention_guard_decided";
const KEY_ENABLED = "reclaim_attention_guard_enabled";

export function getAttentionGuardEnabled() {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(KEY_ENABLED) === "true";
}

export function isAttentionGuardDecided() {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(KEY_DECIDED) === "true";
}

export function setAttentionGuardChoice(enabled) {
  try {
    localStorage.setItem(KEY_DECIDED, "true");
    localStorage.setItem(KEY_ENABLED, enabled ? "true" : "false");
  } catch {}
}

export default function AttentionGuardConsent({ onDecided }) {
  // ESC to decline gracefully
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setAttentionGuardChoice(false);
        onDecided?.(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDecided]);

  const choose = (enabled) => {
    setAttentionGuardChoice(enabled);
    onDecided?.(enabled);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="fixed inset-0 z-[60] flex items-center justify-center px-6"
        style={{
          background: "rgba(13,27,42,0.92)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
        data-testid="attention-guard-consent"
      >
        <motion.div
          initial={{ y: 16, scale: 0.985 }}
          animate={{ y: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-sm rounded-3xl px-7 py-9"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow:
              "0 24px 60px rgba(0,0,0,0.45), 0 0 60px rgba(77,163,255,0.08)",
          }}
        >
          <p
            className="text-[10px] uppercase tracking-[0.42em] mb-4"
            style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
          >
            Optional · Attention Anchor
          </p>

          <h2
            className="italic mb-3"
            style={{
              color: "#FFFFFF",
              fontFamily: JOURNEY_FONT_HEAD,
              fontWeight: 400,
              fontSize: 24,
              letterSpacing: "-0.005em",
              lineHeight: 1.25,
            }}
          >
            Would you like the dot to gently call you back if your eyes drift?
          </h2>

          <p
            className="mb-6"
            style={{
              color: "rgba(255,255,255,0.72)",
              fontFamily: JOURNEY_FONT_BODY,
              fontWeight: 400,
              fontSize: 13.5,
              lineHeight: 1.55,
            }}
          >
            Uses your front camera to notice when your gaze leaves the fixation
            point. The dot pulses once to bring you back. Everything stays on
            this device — no recording, nothing sent anywhere.
          </p>

          <div className="space-y-3">
            <motion.button
              onClick={() => choose(true)}
              whileTap={{ scale: 0.985 }}
              whileHover={{ scale: 1.012 }}
              transition={{ type: "spring", stiffness: 280, damping: 24 }}
              className="w-full rounded-full py-3 flex items-center justify-center gap-2"
              style={{
                background: `linear-gradient(135deg, ${P.accentBlue} 0%, ${P.accentBlueDeep} 100%)`,
                color: "#FFFFFF",
                fontFamily: JOURNEY_FONT_BODY,
                fontWeight: 500,
                fontSize: 14,
                border: "1px solid rgba(77,163,255,0.55)",
                boxShadow:
                  "0 12px 30px rgba(46,123,214,0.40), 0 0 24px rgba(77,163,255,0.30)",
              }}
              data-testid="attention-guard-allow"
            >
              <Eye size={15} strokeWidth={2.2} />
              Yes, keep me anchored
            </motion.button>
            <motion.button
              onClick={() => choose(false)}
              whileTap={{ scale: 0.985 }}
              transition={{ type: "spring", stiffness: 280, damping: 24 }}
              className="w-full rounded-full py-3 flex items-center justify-center gap-2"
              style={{
                background: "rgba(255,255,255,0.04)",
                color: "rgba(255,255,255,0.78)",
                fontFamily: JOURNEY_FONT_BODY,
                fontWeight: 500,
                fontSize: 14,
                border: "1px solid rgba(255,255,255,0.10)",
              }}
              data-testid="attention-guard-decline"
            >
              <EyeOff size={15} strokeWidth={2.2} />
              No thanks
            </motion.button>
          </div>

          <p
            className="text-center mt-5 text-[10.5px] uppercase tracking-[0.32em]"
            style={{ color: "rgba(255,255,255,0.40)", fontFamily: JOURNEY_FONT_LABEL }}
          >
            you can change this any time in settings
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
